import { Box, Chip, FormControl, FormControlLabel, Grid, InputLabel, MenuItem, Select, Slider, Switch, Tab, Tabs, TextField, Typography } from '@mui/material';
import grey from '@mui/material/colors/grey';
import { makeStyles } from '@mui/styles';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { DataAPIContext } from '../api';
import BlockRedirect from '../components/BlockRedirect';
import FixedLinearProgress from '../components/FixedLinearProgress';
import FormActionButtons from '../components/FormActionButtons';
import PageMeta from '../components/PageMeta';
import { TabPanel, a11yProps } from '../components/TabPanel';
import { ContextStore } from '../store/contextStore';
import AutoReplyRulesTable from './AutoReplyRulesTable';
import KnowledgeBaseManager from './KnowledgeBaseManager';
import WebWidgetContactTeam from './WebWidgetContactTeam';
import WebWidgetIntakeAddresses from './WebWidgetIntakeAddresses';
import WebWidgetSettings, { widgetConfigFrom } from './WebWidgetSettings';
import WebWidgetSwitcher, { selectedWidget, useWebWidgets } from './WebWidgetSwitcher';
import {
  DEFAULT_CONTACT_REQUEST_MESSAGE,
  DEFAULT_CONTACT_THANKS_MESSAGE,
  configFlag,
  defaultFallbackMessage,
  fallbackMessageSent,
} from './webWidgetContact';
import KnowledgeReviewTab from './knowledgeReview/KnowledgeReviewTab';
import AIMetricsTab from './AIMetricsTab';
import { setTrainingInboxCount } from './knowledgeReview/inboxCounts';
import {
  getKnowledgeReviewApi, inboxOperatorFor, isLiveFor, trainingInboxVisibleFor,
} from './knowledgeReview/knowledgeReviewApi';
import useGeneralSettings from './useGeneralSettings';
import { isWebWidgetAvailable } from './webWidgetAvailable';
import { webWidgetSavePayload } from './webWidgetSave';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
    paddingBottom: 0,
    backgroundColor: '#FCFCFE',
    [theme.breakpoints.down('sm')]: { margin: theme.spacing(0) },
  },
  inner: {
    overflowY: 'auto',
    maxHeight: 'calc(100vh - 190px)',
  },
  buttonPanel: {
    backgroundColor: grey[100],
    zIndex: 10,
    [theme.breakpoints.down('sm')]: {
      position: 'fixed', bottom: 0, left: 0, right: 0, height: theme.spacing(8),
    },
  },
}));

// Visual floor for the widget auto-reply confidence slider (hatched restricted
// zone) — mirrors AUTO_REPLY_CONFIDENCE_FLOOR on the AI Suite settings page.
const WIDGET_AUTO_REPLY_CONFIDENCE_FLOOR = 0.8;
const PAUSE_AFTER_MANUAL_UNLIMITED = -1;
const PAUSE_AFTER_MANUAL_OPTIONS = [0, 5, 10, 15, 30, 60, 120, 240, PAUSE_AFTER_MANUAL_UNLIMITED];
const pauseAfterManualLabel = (m) => {
  if (m === PAUSE_AFTER_MANUAL_UNLIMITED) return 'Unlimited';
  if (m === 0) return 'No pause';
  return `${m} min`;
};
const formatInheritedPause = (mins) => {
  if (mins === PAUSE_AFTER_MANUAL_UNLIMITED) return 'unlimited';
  if (mins > 0) return `${mins} min`;
  return 'no pause';
};

// Tab indices
const TAB_APPEARANCE = 0;
const TAB_KB = 1;
const TAB_ASSISTANT = 2;
const TAB_TRAINING_INBOX = 3;
const TAB_METRICS = 4;

// URL-driven tabs (same pattern as GeneralTabs): /settings/web-widget/<slug>.
// Keeps the active tab across refreshes and makes tabs deep-linkable.
const TAB_SLUGS = {
  appearance: TAB_APPEARANCE,
  'knowledge-base': TAB_KB,
  // Rules used to be its own tab; it now lives on the Knowledge Base tab (matching
  // the AI Suite layout), so old /rules links land there.
  rules: TAB_KB,
  assistant: TAB_ASSISTANT,
  'training-inbox': TAB_TRAINING_INBOX,
  metrics: TAB_METRICS,
};
const TAB_INDEX_TO_SLUG = {
  [TAB_APPEARANCE]: 'appearance',
  [TAB_KB]: 'knowledge-base',
  [TAB_ASSISTANT]: 'assistant',
  [TAB_TRAINING_INBOX]: 'training-inbox',
  [TAB_METRICS]: 'metrics',
};

/**
 * Standalone "Web Chat Widget" page. Tabs split the widget into distinct controls:
 *  - Appearance / Assistant: config fields persisted through useGeneralSettings'
 *    page-level Save/Cancel (PUT /tour_operator/{id}/ with the changed widget keys only).
 *  - Knowledge Base / Rules: the widget's OWN AI knowledge base (channel="widget")
 *    and auto-reply rules (source_scope pinned to "web_widget"). These self-persist
 *    on each action (their own API calls), independent of the Save/Cancel bar.
 *  - AI Training Inbox: the widget's own Knowledge Review queue (channel="widget"),
 *    separate from the support one in AI Suite. Also self-persisting.
 *
 * This makes the web widget a feature distinct from the support AI Suite, with its
 * own KB and rules that never touch the support auto-responder.
 */
const WebWidgetPage = () => {
  const classes = useStyles();
  const {
    top, syncActive, hasFormChanges, loadedTop,
    onSubfieldChange, setTopAndDirty, saveChanges, onCancel,
  } = useGeneralSettings();

  // CP-8187: the whole page — config, knowledge base and rules alike — is gated on
  // the web_widget.* scope rather than on tour_operator.write (every operator
  // setting) and ai.* (the support auto-responder). The backend enforces the same
  // split per object, so a group holding only web_widget.* can run the website
  // chatbot and nothing else.
  const {
    hasPerm, tourOperator, isFranchise, licensees,
  } = useContext(ContextStore);
  const hasReadPerm = hasPerm('web_widget', 'read');
  const hasWritePerm = hasPerm('web_widget', 'write');
  const hasDeletePerm = hasPerm('web_widget', 'delete');
  // Last-saved widget blob (loadedTop refreshes on load and on save). The
  // Appearance tab uses it to tell saved rows — deleting those needs
  // web_widget.delete — from rows added this session, which stay removable
  // with write alone.
  // CP-8335: operators with several branded widgets pick one (?widget=<key>); every tab
  // then shows that widget. Empty list = the single widget it always had.
  const location = useLocation();
  const history = useHistory();
  const widgets = useWebWidgets(top && top.code);
  const widgetKeyParam = new URLSearchParams(location.search).get('widget');
  const widget = selectedWidget(widgets, widgetKeyParam);
  const setWidget = (w) => {
    const params = new URLSearchParams(location.search);
    if (w && !w.is_default) params.set('widget', w.key); else params.delete('widget');
    const qs = params.toString();
    history.replace(`${location.pathname}${qs ? `?${qs}` : ''}`);
  };
  const savedConfig = useMemo(() => {
    try {
      return JSON.parse(loadedTop).config || {};
    } catch (e) {
      return {};
    }
  }, [loadedTop]);
  const savedWidgetCfg = useMemo(() => widgetConfigFrom(savedConfig, widget), [savedConfig, widget]);
  // WP-H: Save PUTs only the widget keys changed since the last load or save, as {config: {...}},
  // never the whole operator: the backend refuses anything else from a user without
  // tour_operator.write, and widget-only users hold web_widget.write alone. The answer is the
  // whole operator, which refreshes the page's state as before.
  const onSave = () => {
    if (!hasFormChanges) return;
    saveChanges(undefined, undefined, webWidgetSavePayload(top && top.config, savedConfig));
  };
  // Platform gate (same check as the menu entry): set by Curated Planet staff via
  // admin/shell, dashboard-read-only on the backend.
  const widgetAvailable = isWebWidgetAvailable(tourOperator);

  // AI Training Inbox: always visible outside production; in production only with
  // `knowledge_review_enabled` (same gate as the AI Suite one). Sample data unless
  // `knowledge_review_live`. A franchise switches it on per licensee (the one being edited).
  const dataAPI = useContext(DataAPIContext);
  const inboxOperator = inboxOperatorFor(tourOperator, isFranchise, licensees, top?.id);
  const canSeeTrainingInbox = trainingInboxVisibleFor(inboxOperator);
  const [trainingCount, setTrainingCount] = useState(null);

  // Active tab derives from the URL (/settings/web-widget/<slug>), so a refresh
  // stays on the current tab. An unknown/missing slug falls back to Appearance.
  const tabSlug = (location.pathname.replace(/\/$/, '').split('/').pop() || '').toLowerCase();
  const slugTab = TAB_SLUGS[tabSlug] ?? TAB_APPEARANCE;
  const tab = slugTab === TAB_TRAINING_INBOX && !canSeeTrainingInbox ? TAB_APPEARANCE : slugTab;
  const setTab = (v) => {
    const slug = TAB_INDEX_TO_SLUG[v] || 'appearance';
    history.replace(`/settings/web-widget/${slug}${location.search || ''}`);
  };
  // Appearance-tab validity (e.g. link buttons need text + a valid URL). Blocks Save.
  const [appearanceValid, setAppearanceValid] = useState(true);
  const topId = top && top.id;
  // Only the config tabs use the page-level Save/Cancel + unsaved-changes guard.
  const isConfigTab = tab === TAB_APPEARANCE || tab === TAB_ASSISTANT;

  // Badge count before the inbox tab is opened; the tab keeps it fresh after.
  useEffect(() => {
    if (!canSeeTrainingInbox || !widgetAvailable || !hasReadPerm || !topId) return undefined;
    let active = true;
    getKnowledgeReviewApi(dataAPI, topId, isLiveFor(inboxOperator)).summary('widget')
      .then((s) => { if (active) setTrainingCount(s?.open ?? null); })
      .catch(() => { });
    return () => { active = false; };
  }, [canSeeTrainingInbox, widgetAvailable, hasReadPerm, topId, dataAPI, inboxOperator]);
  // The main menu's Web Chat Widget badge shows the same number.
  useEffect(() => { setTrainingInboxCount(topId, 'widget', trainingCount); }, [topId, trainingCount]);

  // The menu already hides this entry when the feature is off or web_widget.read is
  // missing, but the route is reachable by URL (and by a stale bookmark after a
  // permission change), so the page states each case rather than rendering an empty
  // widget config. Feature-off is checked first: it's the more fundamental reason.
  if (!widgetAvailable) {
    return (
      <div className={classes.root}>
        <PageMeta title="Web Chat Widget" />
        <Typography variant="body1" sx={{ p: 2 }}>
          The web chat widget is not enabled for this operator. Contact Curated Planet
          support to enable it.
        </Typography>
      </div>
    );
  }
  if (!hasReadPerm) {
    return (
      <div className={classes.root}>
        <PageMeta title="Web Chat Widget" />
        <Typography variant="body1" sx={{ p: 2 }}>
          You do not have permission to view the web chat widget settings.
        </Typography>
      </div>
    );
  }

  return (
    <>
      <BlockRedirect
        shouldBlock={isConfigTab && hasFormChanges}
        message={'Are you sure you want to leave without saving changes?'}
      />
      <div className={classes.root}>
        <PageMeta title="Web Chat Widget" />
        <FixedLinearProgress visibility={syncActive} />

        <WebWidgetSwitcher widgets={widgets} value={widget} onChange={setWidget} />

        <Tabs
          value={tab}
          onChange={(e, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Appearance" {...a11yProps(TAB_APPEARANCE)} />
          <Tab label="Knowledge Base & Rules" {...a11yProps(TAB_KB)} />
          {canSeeTrainingInbox && (
            <Tab
              {...a11yProps(TAB_TRAINING_INBOX)}
              label={(
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                  AI Training Inbox
                  {trainingCount > 0 && <Chip label={trainingCount} size="small" color="primary" sx={{ height: 20, fontSize: 12 }} />}
                </Box>
              )}
            />
          )}
          <Tab label="Assistant" {...a11yProps(TAB_ASSISTANT)} />
          <Tab label="Metrics" {...a11yProps(TAB_METRICS)} />
        </Tabs>

        {/* The capped, internally scrolling panel keeps the config tabs' Save/Cancel bar on
            screen. The training inbox has no such bar and lays out its own sticky list and
            action bar, so it scrolls with the page instead of leaving that space empty. */}
        <div className={tab === TAB_TRAINING_INBOX || tab === TAB_METRICS ? undefined : classes.inner}>
          <TabPanel value={tab} index={TAB_APPEARANCE}>
            <WebWidgetSettings
              top={top}
              onSubfieldChange={onSubfieldChange}
              setTopAndDirty={setTopAndDirty}
              hasWritePerm={hasWritePerm}
              hasDeletePerm={hasDeletePerm}
              savedConfig={savedWidgetCfg}
              hideAssistantInstructions
              onValidityChange={setAppearanceValid}
              widget={widget}
            />
          </TabPanel>

          {/* Rules above the knowledge base -- the same stacked layout users know
              from the AI Suite's Auto-Responses tab. */}
          <TabPanel value={tab} index={TAB_KB}>
            <AutoReplyRulesTable channelScope="web_widget" licenseeId={topId} widgets={widgets} widget={widget} />
            <KnowledgeBaseManager
              channel="widget"
              widgets={widgets}
              widget={widget}
              effectiveTopId={topId}
              hasWrite={hasWritePerm}
              hasDelete={hasDeletePerm}
              webWidgetAvailable
              onOpenTrainingInbox={canSeeTrainingInbox ? () => setTab(TAB_TRAINING_INBOX) : undefined}
            />
          </TabPanel>

          <TabPanel value={tab} index={TAB_TRAINING_INBOX}>
            {canSeeTrainingInbox && (
              <Box sx={{ p: 2 }}>
                <KnowledgeReviewTab
                  key={`${topId}:${widget ? widget.id : ''}`}
                  channel="widget"
                  widgetId={widget && widgets.length > 1 ? widget.id : undefined}
                  licenseeId={topId}
                  onOpenCountChange={setTrainingCount}
                />
              </Box>
            )}
          </TabPanel>

          {/* The AI Metrics page pinned to web chat (CP-8400). Self-contained: no Save bar. */}
          <TabPanel value={tab} index={TAB_METRICS}>
            <Box sx={{ p: 2 }}>
              {tab === TAB_METRICS && (
                <AIMetricsTab
                  key={`${topId}:${widget ? widget.id : ''}`}
                  channelLock="web_widget"
                  widgetId={widget && widgets.length > 1 ? widget.id : undefined}
                />
              )}
            </Box>
          </TabPanel>

          <TabPanel value={tab} index={TAB_ASSISTANT}>
            <Box sx={{ p: 2, maxWidth: 900 }}>
              <Typography variant="h6" sx={{ mb: 0.5 }}>Widget assistant instructions</Typography>
              <Typography variant="body2" sx={{ mb: 2 }} color="text.secondary">
                The system prompt used only for website visitors in the web chat widget. Leave blank
                to use the default public sales assistant. This is separate from the support-chat prompt.
              </Typography>
              <TextField
                disabled={!hasWritePerm}
                variant="filled"
                size="small"
                fullWidth
                multiline
                minRows={4}
                label="Assistant instructions (optional)"
                InputLabelProps={{ shrink: true }}
                placeholder="Leave blank to use the default public sales assistant."
                value={(top && top.config && top.config.chatbot_prompt_web_widget) || ''}
                onChange={(e) => onSubfieldChange('config.chatbot_prompt_web_widget', e.target.value)}
              />

              {(() => {
                const cfgObj = (top && top.config) || {};
                const widgetConfRaw = parseFloat(cfgObj.ai_auto_reply_min_confidence_web_widget);
                const sharedConfRaw = parseFloat(cfgObj.ai_auto_reply_min_confidence);
                const widgetConf = Number.isFinite(widgetConfRaw) ? widgetConfRaw : null;
                const sharedConf = Number.isFinite(sharedConfRaw) ? sharedConfRaw : 0.9;
                const effectiveConf = widgetConf !== null ? widgetConf : sharedConf;
                const effectivePct = Math.round(effectiveConf * 100);
                return (
                  <>
                    <Typography variant="subtitle2" sx={{ mt: 3, fontWeight: 500 }}>
                      Auto-Reply Confidence Threshold
                    </Typography>
                    <Typography variant="caption" sx={{ display: 'block', mb: 1 }}>
                      Adjust the minimum confidence level required for the widget assistant to
                      automatically send a reply. Separate from the support-chat threshold in
                      AI Suite settings.
                      {widgetConf === null && (
                        <> Currently inheriting the support-chat threshold ({Math.round(sharedConf * 100)}%).</>
                      )}
                    </Typography>
                    <Box sx={{ pr: 1, pl: { xs: 1.5, sm: 0 }, maxWidth: 520, marginTop: 4 }}>
                      <Slider
                        value={effectivePct}
                        onChange={(_, v) =>
                          onSubfieldChange('config.ai_auto_reply_min_confidence_web_widget', (Number(v) || 0) / 100)
                        }
                        valueLabelDisplay="on"
                        valueLabelFormat={(v) => `${v}%`}
                        min={0}
                        max={100}
                        step={1}
                        disabled={!hasWritePerm}
                        sx={{
                          '& .MuiSlider-valueLabel': {
                            px: 1,
                            py: 0.25,
                          },
                          // Restricted zone below the floor, hatched out with a boundary
                          // line — mirrors the AI Suite auto-reply threshold slider.
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            left: 0,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            width: `${Math.round(WIDGET_AUTO_REPLY_CONFIDENCE_FLOOR * 100)}%`,
                            height: 6,
                            borderRadius: 3,
                            pointerEvents: 'none',
                            zIndex: 1,
                            backgroundColor: 'action.disabledBackground',
                            backgroundImage:
                              'repeating-linear-gradient(45deg, rgba(0,0,0,0.22) 0, rgba(0,0,0,0.22) 3px, transparent 3px, transparent 7px)',
                            borderRight: '2px solid',
                            borderColor: 'text.disabled',
                          },
                        }}
                      />
                      <Typography variant="caption" component="div" sx={{ mt: -0.3, textAlign: 'left' }}>
                        {/* On phones the two clauses stack vertically (same as AI Suite, CP-7098) */}
                        <Box component="span" sx={{ display: { xs: 'block', sm: 'inline' } }}>
                          <strong>Above {effectivePct}%</strong> → auto-reply.
                        </Box>
                        {' '}
                        <Box component="span" sx={{ display: { xs: 'block', sm: 'inline' } }}>
                          <strong>Below {effectivePct}%</strong> → fallback message.
                        </Box>
                      </Typography>
                    </Box>
                  </>
                );
              })()}

              {(() => {
                const cfgObj = (top && top.config) || {};
                const widgetRaw = parseInt(cfgObj.pause_chatbot_for_x_minutes_after_manual_response_web_widget, 10);
                const sharedRaw = parseInt(cfgObj.pause_chatbot_for_x_minutes_after_manual_response, 10);
                const widgetPause = Number.isFinite(widgetRaw) ? widgetRaw : null;
                const sharedPause = Number.isFinite(sharedRaw) ? sharedRaw : 0;
                return (
                  <>
                    <Typography variant="subtitle2" sx={{ mt: 3, fontWeight: 500 }}>
                      Pause After Agent Reply
                    </Typography>
                    <Typography variant="caption" sx={{ display: 'block', mb: 1 }}>
                      How long the assistant stays quiet in a widget conversation after a human
                      agent replies. Unlimited keeps the bot off for the rest of the conversation.
                      Separate from the support-chat setting.
                      {widgetPause === null && (
                        <> Currently inheriting the support-chat setting
                          ({formatInheritedPause(sharedPause)}).</>
                      )}
                    </Typography>
                    <FormControl variant="filled" size="small" sx={{ width: 200 }} disabled={!hasWritePerm}>
                      {/* shrink: the "Inherit" option has value '' (displayEmpty), which MUI treats as
                          empty and would leave the label floating over the displayed text. */}
                      <InputLabel id="widget-pause-after-agent-label" shrink>Pause auto-reply for</InputLabel>
                      <Select
                        labelId="widget-pause-after-agent-label"
                        label="Pause auto-reply for"
                        displayEmpty
                        value={widgetPause === null ? '' : widgetPause}
                        onChange={(e) => {
                          const v = e.target.value;
                          onSubfieldChange(
                            'config.pause_chatbot_for_x_minutes_after_manual_response_web_widget',
                            v === '' ? null : Number(v)
                          );
                        }}
                      >
                        <MenuItem value="">
                          Inherit ({formatInheritedPause(sharedPause)})
                        </MenuItem>
                        {(
                          widgetPause !== null && !PAUSE_AFTER_MANUAL_OPTIONS.includes(widgetPause)
                            ? [...PAUSE_AFTER_MANUAL_OPTIONS, widgetPause]
                            : PAUSE_AFTER_MANUAL_OPTIONS
                        ).map((m) => (
                          <MenuItem key={m} value={m}>{pauseAfterManualLabel(m)}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </>
                );
              })()}

              <Typography variant="h6" sx={{ mt: 3, mb: 0.5 }}>Fallback message</Typography>
              <Typography variant="body2" sx={{ mb: 2 }} color="text.secondary">
                Sent to the visitor when the assistant hands the conversation to your team (it
                isn&apos;t confident enough to answer), so they never get silence. Leave blank to
                use the built-in text shown. When it asks for the visitor&apos;s email, the email
                request below is added to it.
              </Typography>
              {(() => {
                // The placeholder IS the built-in text a blank field sends, not a hint, and it
                // depends on the settings below (webWidgetContact.defaultFallbackMessage) --
                // rendered darker than MUI's dimmed placeholder, but still a shade lighter than
                // typed text so a saved override remains distinguishable.
                const cfgObj = (top && top.config) || {};
                const asksAlways = configFlag(cfgObj.web_widget_contact_capture, false)
                  && !configFlag(cfgObj.web_widget_contact_capture_outside_office_hours_only, false);
                return (
                  <TextField
                    disabled={!hasWritePerm}
                    variant="filled"
                    size="small"
                    fullWidth
                    multiline
                    minRows={3}
                    label="Fallback message"
                    InputLabelProps={{ shrink: true }}
                    sx={{ '& .MuiInputBase-input::placeholder': { color: 'text.secondary', opacity: 1 } }}
                    placeholder={defaultFallbackMessage(cfgObj, { askForEmail: asksAlways })}
                    value={cfgObj.ai_auto_reply_fallback_message_web_widget || ''}
                    onChange={(e) => onSubfieldChange('config.ai_auto_reply_fallback_message_web_widget', e.target.value)}
                  />
                );
              })()}

              {(() => {
                // Unset means on (backend pause_chatbot_after_widget_handoff_enabled).
                const raw = top && top.config ? top.config.pause_chatbot_after_handoff_web_widget : undefined;
                const quietAfterHandoff = configFlag(raw, true);
                return (
                  <Box sx={{ mt: 2 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          id="widget-quiet-after-handoff"
                          checked={quietAfterHandoff}
                          disabled={!hasWritePerm}
                          onChange={(e) => onSubfieldChange('config.pause_chatbot_after_handoff_web_widget', e.target.checked)}
                        />
                      }
                      label={<Typography variant="subtitle2" sx={{ fontWeight: 500 }}>Go quiet after handing off</Typography>}
                    />
                    <Typography variant="caption" sx={{ display: 'block', ml: 6, mt: -0.5 }}>
                      After sending the fallback message, the assistant stops answering in that
                      conversation until a team member replies or someone resumes it from the chat.
                      Off: it keeps answering, but never sends the fallback message twice in a row.
                    </Typography>
                  </Box>
                );
              })()}

              {(() => {
                // Contact handoff (backend demo/lib/web_widget_contact.py). Capture is opt-in
                // (unset = off); the visitor's confirmation email is on unless turned off.
                const cfgObj = (top && top.config) || {};
                const captureOn = configFlag(cfgObj.web_widget_contact_capture, false);
                const outsideHoursOnly = configFlag(cfgObj.web_widget_contact_capture_outside_office_hours_only, false);
                const confirmationOn = configFlag(cfgObj.web_widget_contact_confirmation_email, true);
                // What the visitor actually gets, opening and email request together.
                const previews = outsideHoursOnly
                  ? [
                    { label: 'Outside office hours', text: fallbackMessageSent(cfgObj, { askForEmail: true }) },
                    { label: 'During office hours', text: fallbackMessageSent(cfgObj, { askForEmail: false }) },
                  ]
                  : [{ label: null, text: fallbackMessageSent(cfgObj, { askForEmail: true }) }];
                const placeholderSx = { '& .MuiInputBase-input::placeholder': { color: 'text.secondary', opacity: 1 } };
                return (
                  <Box sx={{ mt: 2 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          id="widget-contact-capture"
                          checked={captureOn}
                          disabled={!hasWritePerm}
                          onChange={(e) => onSubfieldChange('config.web_widget_contact_capture', e.target.checked)}
                        />
                      }
                      label={<Typography variant="subtitle2" sx={{ fontWeight: 500 }}>Ask for the visitor&apos;s email when handing off</Typography>}
                    />
                    <Typography variant="caption" sx={{ display: 'block', ml: 6, mt: -0.5 }}>
                      The fallback message, and the reply of any trigger set to &quot;Pass to team&quot;, asks for
                      an email, the widget shows an email form, and the team gets the full chat by email once
                      the visitor leaves an address.
                    </Typography>
                    {captureOn && (
                      <Box sx={{ ml: 6, mt: 1 }}>
                        <FormControlLabel
                          control={
                            <Switch
                              id="widget-contact-capture-outside-hours"
                              checked={outsideHoursOnly}
                              disabled={!hasWritePerm}
                              onChange={(e) => onSubfieldChange(
                                'config.web_widget_contact_capture_outside_office_hours_only', e.target.checked,
                              )}
                            />
                          }
                          label={<Typography variant="subtitle2" sx={{ fontWeight: 500 }}>Only ask outside office hours</Typography>}
                        />
                        <Typography variant="caption" sx={{ display: 'block', ml: 6, mt: -0.5 }}>
                          During the hours set on the Office Hours page, the fallback message doesn&apos;t
                          ask for an email: your team answers in the chat. With no office hours set, it
                          always asks.
                        </Typography>
                      </Box>
                    )}
                    {captureOn && <WebWidgetContactTeam operatorCode={(top && top.code) || ''} />}
                    {captureOn && (
                      <Box sx={{ ml: 6, mt: 2 }}>
                        <TextField
                          disabled={!hasWritePerm}
                          variant="filled"
                          size="small"
                          fullWidth
                          multiline
                          minRows={2}
                          label="Email request (added to the fallback message)"
                          InputLabelProps={{ shrink: true }}
                          // Like the fallback message: the placeholder is the effective default.
                          sx={placeholderSx}
                          placeholder={DEFAULT_CONTACT_REQUEST_MESSAGE}
                          value={cfgObj.web_widget_contact_request_message || ''}
                          onChange={(e) => onSubfieldChange('config.web_widget_contact_request_message', e.target.value)}
                        />
                        <Box data-testid="fallback-message-preview" sx={{ mt: 2 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                            What visitors get when the assistant hands off
                          </Typography>
                          {previews.map((p) => (
                            <Box key={p.label || 'always'} sx={{ mt: 1 }}>
                              {p.label && <Typography variant="caption" color="text.secondary">{p.label}</Typography>}
                              <Typography
                                variant="body2"
                                sx={{
                                  whiteSpace: 'pre-wrap',
                                  bgcolor: 'action.hover',
                                  borderLeft: 3,
                                  borderColor: 'divider',
                                  borderRadius: 1,
                                  px: 1.5,
                                  py: 1,
                                }}
                              >
                                {p.text}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                        <TextField
                          disabled={!hasWritePerm}
                          variant="filled"
                          size="small"
                          fullWidth
                          multiline
                          minRows={2}
                          label="Thank-you message"
                          InputLabelProps={{ shrink: true }}
                          sx={{ ...placeholderSx, mt: 2 }}
                          placeholder={DEFAULT_CONTACT_THANKS_MESSAGE}
                          helperText="{email} and {name} are replaced; {, name} adds ', Anna' only when a name was given."
                          value={cfgObj.web_widget_contact_thanks_message || ''}
                          onChange={(e) => onSubfieldChange('config.web_widget_contact_thanks_message', e.target.value)}
                        />
                        <FormControlLabel
                          sx={{ mt: 1 }}
                          control={
                            <Switch
                              id="widget-contact-confirmation-email"
                              checked={confirmationOn}
                              disabled={!hasWritePerm}
                              onChange={(e) => onSubfieldChange('config.web_widget_contact_confirmation_email', e.target.checked)}
                            />
                          }
                          label={<Typography variant="subtitle2" sx={{ fontWeight: 500 }}>Email the visitor a copy of the conversation</Typography>}
                        />
                      </Box>
                    )}
                  </Box>
                );
              })()}

              <WebWidgetIntakeAddresses operatorCode={(top && top.code) || ''} />
            </Box>
          </TabPanel>
        </div>
      </div>

      {/* Save / Cancel — only the config tabs have page-level unsaved changes. */}
      {isConfigTab && (
        <Grid item xs={12} className={classes.buttonPanel} container alignItems="center" py={2} px={2}>
          <FormActionButtons
            mode="edit"
            size="medium"
            sx={{ width: '100%' }}
            onCancel={onCancel}
            cancelDisabled={syncActive || !hasFormChanges}
            onSubmit={onSave}
            submitDisabled={!hasWritePerm || syncActive || !hasFormChanges || !appearanceValid}
          />
        </Grid>
      )}
    </>
  );
};

export default WebWidgetPage;
